import React, { useState, useEffect, useCallback } from 'react';
import {
  ArrowLeft,
  Plus,
  RefreshCw,
  Trash2,
  Loader2,
  Rss,
  Globe,
  FileText,
  AlertCircle,
  ChevronRight,
  ExternalLink,
  Download,
  Check,
  Link,
  Copy,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { feedWatchService, FEED_TYPE_FORMATS } from '../../services/feedWatch';
import { PlatformBadge } from './ShopList';
import { StatusBadge } from './FeedList';
import type { Shop, Feed, FeedType, ExportFormat } from '../../services/feedWatch';

// ── Feed Type Badge ─────────────────────────────────────────────

const FEED_TYPE_LABELS: Record<FeedType, { label: string; className: string }> = {
  google_shopping: { label: 'Google', className: 'bg-blue-500/10 text-blue-500 border-blue-500/20' },
  meta: { label: 'Meta', className: 'bg-indigo-500/10 text-indigo-500 border-indigo-500/20' },
  tiktok: { label: 'TikTok', className: 'bg-pink-500/10 text-pink-500 border-pink-500/20' },
  amazon: { label: 'Amazon', className: 'bg-orange-500/10 text-orange-500 border-orange-500/20' },
};

function FeedTypeBadge({ feedType }: { feedType?: FeedType }) {
  const type = feedType ?? 'google_shopping';
  const config = FEED_TYPE_LABELS[type];
  return <Badge variant="outline" className={config.className}>{config.label}</Badge>;
}

// ── Create Feed Dialog (scoped to shop) ─────────────────────────

interface CreateFeedDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreate: (data: { name: string; shop_id: string; source_url?: string; source_type?: 'url' | 'paste'; raw_xml?: string; feed_type?: FeedType }) => void;
  shopId: string;
  isCreating: boolean;
}

function CreateFeedDialog({ open, onOpenChange, onCreate, shopId, isCreating }: CreateFeedDialogProps) {
  const [name, setName] = useState('');
  const [feedType, setFeedType] = useState<FeedType>('google_shopping');
  const [sourceType, setSourceType] = useState<'url' | 'paste'>('url');
  const [sourceUrl, setSourceUrl] = useState('');
  const [rawXml, setRawXml] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const data: { name: string; shop_id: string; source_url?: string; source_type?: 'url' | 'paste'; raw_xml?: string; feed_type?: FeedType } = {
      name,
      shop_id: shopId,
      feed_type: feedType,
    };
    if (sourceType === 'url') {
      data.source_url = sourceUrl;
    } else {
      data.source_type = 'paste';
      data.raw_xml = rawXml;
    }
    onCreate(data);
    setName('');
    setSourceUrl('');
    setRawXml('');
  };

  const canSubmit = name.trim() && (sourceType === 'url' ? sourceUrl.trim() : rawXml.trim());

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>New Collection</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <Label htmlFor="feed-name">Collection Name</Label>
            <Input
              id="feed-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="My Product Collection"
              className="mt-1.5 h-11 rounded-xl border-2 focus:border-primary focus:ring-2 focus:ring-primary/20"
            />
          </div>

          <div>
            <Label>Channel</Label>
            <Select value={feedType} onValueChange={(v) => setFeedType(v as FeedType)}>
              <SelectTrigger className="mt-1.5 h-11 rounded-xl border-2 focus:border-primary focus:ring-2 focus:ring-primary/20">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="google_shopping">Google Shopping</SelectItem>
                <SelectItem value="meta">Meta / Facebook</SelectItem>
                <SelectItem value="tiktok">TikTok</SelectItem>
                <SelectItem value="amazon">Amazon</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Source</Label>
            <div className="flex gap-2 mt-1.5">
              <Button
                type="button"
                variant={sourceType === 'url' ? 'default' : 'outline'}
                size="sm"
                className="rounded-lg"
                onClick={() => setSourceType('url')}
              >
                <Globe className="h-4 w-4 mr-1.5" />
                URL
              </Button>
              <Button
                type="button"
                variant={sourceType === 'paste' ? 'default' : 'outline'}
                size="sm"
                className="rounded-lg"
                onClick={() => setSourceType('paste')}
              >
                <FileText className="h-4 w-4 mr-1.5" />
                Paste XML
              </Button>
            </div>
          </div>

          {sourceType === 'url' ? (
            <div>
              <Label htmlFor="source-url">Collection URL</Label>
              <Input
                id="source-url"
                value={sourceUrl}
                onChange={(e) => setSourceUrl(e.target.value)}
                placeholder="https://mystore.com/products.xml"
                className="mt-1.5 h-11 rounded-xl border-2 focus:border-primary focus:ring-2 focus:ring-primary/20"
              />
            </div>
          ) : (
            <div>
              <Label htmlFor="raw-xml">XML Content</Label>
              <Textarea
                id="raw-xml"
                value={rawXml}
                onChange={(e) => setRawXml(e.target.value)}
                placeholder="<rss>...</rss>"
                rows={6}
                className="mt-1.5 font-mono text-xs rounded-xl border-2 focus:border-primary focus:ring-2 focus:ring-primary/20"
              />
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-lg" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" className="rounded-lg" disabled={!canSubmit || isCreating}>
              {isCreating && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
              Create Collection
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Delete Shop Confirm Dialog ──────────────────────────────────

interface DeleteShopDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  shopName: string;
  feedCount: number;
  isDeleting: boolean;
}

function DeleteShopDialog({ open, onOpenChange, onConfirm, shopName, feedCount, isDeleting }: DeleteShopDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Delete Shop</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Are you sure you want to delete <span className="font-semibold text-foreground">{shopName}</span>?
          </p>
          {feedCount > 0 && (
            <div className="flex items-start gap-2 p-3 bg-destructive/10 rounded-lg border border-destructive/20">
              <AlertCircle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
              <p className="text-sm text-destructive">
                This will permanently delete all <span className="font-semibold">{feedCount}</span>{' '}
                {feedCount === 1 ? 'collection' : 'collections'} and their data (products, mappings, exports, designs).
              </p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-lg" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            className="rounded-lg"
            onClick={onConfirm}
            disabled={isDeleting}
          >
            {isDeleting && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
            Delete Shop
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Quick Export Button ──────────────────────────────────────────

function QuickExport({ feed }: { feed: Feed }) {
  const [exportingFormat, setExportingFormat] = useState<ExportFormat | null>(null);
  const [doneFormat, setDoneFormat] = useState<ExportFormat | null>(null);
  const [publicUrl, setPublicUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const pollRef = React.useRef<NodeJS.Timeout | null>(null);
  const copyTimerRef = React.useRef<NodeJS.Timeout | null>(null);

  // Fetch latest completed export to get public URL
  React.useEffect(() => {
    if (feed.status !== 'ready') return;
    (async () => {
      try {
        const list = await feedWatchService.listExports(feed.id);
        const exports = Array.isArray(list) ? list : [];
        const latest = exports.find((e) => e.status === 'completed');
        if (latest) {
          const detail = await feedWatchService.getExport(feed.id, latest.id);
          if (detail.public_url) setPublicUrl(detail.public_url);
        }
      } catch {
        // no exports yet
      }
    })();
  }, [feed.id, feed.status]);

  React.useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    };
  }, []);

  if (feed.status !== 'ready') return null;

  const formats = FEED_TYPE_FORMATS[feed.feed_type] ?? ['xml', 'csv'];

  const handleCopyUrl = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!publicUrl) return;
    navigator.clipboard.writeText(publicUrl);
    setCopied(true);
    if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    copyTimerRef.current = setTimeout(() => setCopied(false), 2000);
  };

  const handleExport = async (format: ExportFormat) => {
    try {
      setExportingFormat(format);
      setDoneFormat(null);
      const exp = await feedWatchService.createExport(feed.id, format);

      // Poll until complete
      pollRef.current = setInterval(async () => {
        try {
          const updated = await feedWatchService.getExport(feed.id, exp.id);
          if (updated.status === 'completed') {
            if (pollRef.current) clearInterval(pollRef.current);
            pollRef.current = null;
            setExportingFormat(null);
            setDoneFormat(format);
            setTimeout(() => setDoneFormat(null), 3000);

            // Update public URL
            if (updated.public_url) setPublicUrl(updated.public_url);

            // Auto-download
            if (updated.download_url) {
              const response = await fetch(updated.download_url);
              const blob = await response.blob();
              const blobUrl = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = blobUrl;
              a.download = `${feed.name}.${format}`;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              URL.revokeObjectURL(blobUrl);
            }
          } else if (updated.status === 'failed') {
            if (pollRef.current) clearInterval(pollRef.current);
            pollRef.current = null;
            setExportingFormat(null);
          }
        } catch {
          // keep polling
        }
      }, 2000);
    } catch {
      setExportingFormat(null);
    }
  };

  return (
    <div className="flex items-center gap-1.5">
      {formats.map((fmt) => {
        const isExporting = exportingFormat === fmt;
        const isDone = doneFormat === fmt;
        return (
          <button
            key={fmt}
            onClick={(e) => {
              e.stopPropagation();
              if (!isExporting) handleExport(fmt);
            }}
            disabled={exportingFormat !== null}
            className={`
              inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md border transition-all duration-200
              ${isDone
                ? 'bg-green-500/10 text-green-600 border-green-500/30'
                : isExporting
                  ? 'bg-primary/5 text-primary border-primary/20 cursor-wait'
                  : 'bg-background text-muted-foreground border-border hover:text-foreground hover:border-primary/40 hover:bg-primary/5'
              }
              disabled:opacity-40 disabled:cursor-not-allowed
            `}
            title={`Export as ${fmt.toUpperCase()}`}
          >
            {isExporting ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : isDone ? (
              <Check className="h-3 w-3" />
            ) : (
              <Download className="h-3 w-3" />
            )}
            {fmt.toUpperCase()}
          </button>
        );
      })}
      {publicUrl && (
        <button
          onClick={handleCopyUrl}
          className={`
            inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md border transition-all duration-200
            ${copied
              ? 'bg-green-500/10 text-green-600 border-green-500/30'
              : 'bg-background text-muted-foreground border-border hover:text-foreground hover:border-primary/40 hover:bg-primary/5'
            }
          `}
          title={publicUrl}
        >
          {copied ? <Check className="h-3 w-3" /> : <Link className="h-3 w-3" />}
          {copied ? 'Copied' : 'URL'}
        </button>
      )}
    </div>
  );
}

// ── Shop Detail ─────────────────────────────────────────────────

interface ShopDetailProps {
  shop: Shop;
  onBack: () => void;
  onSelectFeed: (feed: Feed) => void;
  onShopDeleted: () => void;
}

export function ShopDetail({ shop, onBack, onSelectFeed, onShopDeleted }: ShopDetailProps) {
  const [feeds, setFeeds] = useState<Feed[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [feedDialogOpen, setFeedDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const loadFeeds = useCallback(async () => {
    try {
      setIsLoading(true);
      const result = await feedWatchService.listShopFeeds(shop.id);
      setFeeds(result.data);
    } catch {
      // handled
    } finally {
      setIsLoading(false);
    }
  }, [shop.id]);

  useEffect(() => {
    loadFeeds();
  }, [loadFeeds]);

  const handleCreateFeed = async (data: { name: string; shop_id: string; source_url?: string; source_type?: 'url' | 'paste'; raw_xml?: string; feed_type?: FeedType }) => {
    try {
      setIsCreating(true);
      const feed = await feedWatchService.createFeed(data);
      setFeeds((prev) => [feed, ...prev]);
      setFeedDialogOpen(false);
      onSelectFeed(feed);
    } catch {
      // handled
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteShop = async () => {
    try {
      setIsDeleting(true);
      await feedWatchService.deleteShop(shop.id);
      onShopDeleted();
    } catch {
      setIsDeleting(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Breadcrumb */}
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors duration-150 group"
      >
        <ArrowLeft className="h-4 w-4 group-hover:-translate-x-0.5 transition-transform duration-150" />
        <span>Back to shops</span>
      </button>

      {/* Shop header */}
      <div className="flex items-end justify-between">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-3xl font-bold tracking-tight">{shop.name}</h1>
            <PlatformBadge platform={shop.platform} />
          </div>
          {shop.shop_url && (
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground mt-1">
              <ExternalLink className="h-3.5 w-3.5" />
              <span>{shop.shop_url}</span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" className="rounded-lg" onClick={loadFeeds} disabled={isLoading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button className="rounded-lg" onClick={() => setFeedDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            New Collection
          </Button>
          <Button
            variant="outline"
            className="rounded-lg text-destructive hover:text-destructive hover:bg-destructive/10"
            onClick={() => setDeleteDialogOpen(true)}
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete
          </Button>
        </div>
      </div>

      {/* Feeds table */}
      {isLoading && feeds.length === 0 ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" />
        </div>
      ) : feeds.length === 0 ? (
        <Card className="flex flex-col items-center justify-center py-20 text-center border-2 border-dashed rounded-xl">
          <Rss className="h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-1">No collections yet</h3>
          <p className="text-muted-foreground mb-6">
            Create a collection from a URL or paste XML to get started.
          </p>
          <Button className="rounded-lg" onClick={() => setFeedDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Create First Collection
          </Button>
        </Card>
      ) : (
        <Card className="border-2 shadow-lg rounded-xl overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="text-left px-6 py-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Name</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Channel</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Status</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Products</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Fields</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Source</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Last Fetched</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Export</th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/50">
              {feeds.map((feed) => (
                <tr
                  key={feed.id}
                  className="group hover:bg-muted/30 cursor-pointer transition-colors duration-150"
                  onClick={() => onSelectFeed(feed)}
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                        <Rss className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <span className="text-sm font-semibold">{feed.name}</span>
                        {feed.status === 'error' && feed.error_message && (
                          <div className="flex items-center gap-1.5 mt-0.5 text-xs text-destructive">
                            <AlertCircle className="h-3 w-3 shrink-0" />
                            <span className="line-clamp-1">{feed.error_message}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <FeedTypeBadge feedType={feed.feed_type} />
                  </td>
                  <td className="px-6 py-4">
                    <StatusBadge status={feed.status} />
                  </td>
                  <td className="px-6 py-4 text-sm tabular-nums">
                    {feed.total_products.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 text-sm text-muted-foreground tabular-nums">
                    {feed.detected_fields.length}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                      {feed.source_url ? (
                        <><Globe className="h-3.5 w-3.5 shrink-0" /> URL</>
                      ) : (
                        <><FileText className="h-3.5 w-3.5 shrink-0" /> Paste</>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">
                    {feed.last_fetched_at
                      ? new Date(feed.last_fetched_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                      : '-'}
                  </td>
                  <td className="px-6 py-4">
                    <QuickExport feed={feed} />
                  </td>
                  <td className="px-4 py-4">
                    <ChevronRight className="h-4 w-4 text-muted-foreground/50 group-hover:text-foreground transition-colors" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <CreateFeedDialog
        open={feedDialogOpen}
        onOpenChange={setFeedDialogOpen}
        onCreate={handleCreateFeed}
        shopId={shop.id}
        isCreating={isCreating}
      />

      <DeleteShopDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleDeleteShop}
        shopName={shop.name}
        feedCount={shop.feed_count}
        isDeleting={isDeleting}
      />
    </div>
  );
}
